import { useState, useRef, useCallback } from 'react';
import reviewService from '../../services/reviewService';
import './ImageUpload.css';

const MAX_IMAGES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const ImageUpload = ({ images = [], onChange, disabled = false }) => {
  const [uploading, setUploading] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const showError = (message) => {
    const errorId = Date.now();
    setErrors(prev => ({ ...prev, [errorId]: message }));

    setTimeout(() => {
      setErrors(prev => {
        const { [errorId]: _, ...rest } = prev;
        return rest;
      });
    }, 4000);
  };

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      showError('Invalid format. Use JPEG, PNG, or WebP.');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      showError('File too large. Max 5MB.');
      return false;
    }

    return true;
  };

  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files);

    // Check max images limit
    if (images.length + fileArray.length > MAX_IMAGES) {
      showError(`Maximum ${MAX_IMAGES} images allowed.`);
      return;
    }

    // Process each file
    for (const file of fileArray) {
      if (!validateFile(file)) {
        continue;
      }

      const uploadId = Date.now() + Math.random();

      // Add to uploading state
      setUploading(prev => ({ ...prev, [uploadId]: { progress: 0, file } }));

      try {
        // Upload image
        const result = await reviewService.uploadImage(file);

        // Add to images array
        onChange([...images, {
          url: result.url,
          thumbnailUrl: result.thumbnailUrl || result.url,
          fileSize: result.fileSize,
          width: result.width,
          height: result.height
        }]);
      } catch (error) {
        console.error('Upload error:', error);
        showError('Upload failed. Please try again.');
      } finally {
        // Remove from uploading state
        setUploading(prev => {
          const { [uploadId]: _, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileSelect(e.target.files);
      e.target.value = ''; // Reset input
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (!disabled && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const handlePaste = useCallback((e) => {
    if (disabled) return;

    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      handleFileSelect(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  const handleDelete = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onChange(newImages);
  };

  const totalSlots = MAX_IMAGES;
  const emptySlots = Math.max(0, totalSlots - images.length - Object.keys(uploading).length);

  return (
    <div className="image-upload-container" onPaste={handlePaste}>
      <div className="image-upload-label">
        Add Photos (max {MAX_IMAGES}, 5MB each)
      </div>

      <div className="image-upload-grid">
        {/* Existing images */}
        {images.map((image, index) => (
          <div key={`image-${index}`} className="image-slot uploaded">
            <img src={image.thumbnailUrl || image.url} alt={`Upload ${index + 1}`} />
            <button
              type="button"
              className="delete-button"
              onClick={() => handleDelete(index)}
              aria-label="Delete image"
              disabled={disabled}
            >
              ×
            </button>
          </div>
        ))}

        {/* Uploading slots */}
        {Object.entries(uploading).map(([uploadId, data]) => (
          <div key={uploadId} className="image-slot uploading">
            <div className="upload-progress">
              <div className="progress-spinner"></div>
              <div className="progress-text">Uploading...</div>
            </div>
          </div>
        ))}

        {/* Empty slots */}
        {Array.from({ length: emptySlots }).map((_, index) => (
          <div
            key={`empty-${index}`}
            className={`image-slot empty ${dragActive ? 'drag-active' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={handleClick}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            role="button"
            tabIndex={disabled ? -1 : 0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClick();
              }
            }}
          >
            <div className="empty-content">
              <span className="plus-icon">+</span>
              <span className="empty-text">
                {index === 0 ? (
                  <>
                    Click to upload
                    <br />
                    or drag & drop
                  </>
                ) : (
                  'Add image'
                )}
              </span>
            </div>
          </div>
        ))}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_TYPES.join(',')}
        multiple
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {/* Error messages */}
      {Object.entries(errors).length > 0 && (
        <div className="image-upload-errors">
          {Object.entries(errors).map(([id, message]) => (
            <div key={id} className="error-message" role="alert">
              {message}
            </div>
          ))}
        </div>
      )}

      <div className="image-upload-hint">
        Tip: You can also paste images with Ctrl+V (Cmd+V on Mac)
      </div>
    </div>
  );
};

export default ImageUpload;
